import {
  Activity,
  ActivityButtonVisualStyle,
  ActivityDetailPageRes,
  ActivityHomeBannerCardViewModel,
  ActivityV2ButtonConfig,
  ActivityV2BannerConfig,
  ActivityV2LandingComponent,
  ActivityV2PreviewConfig,
} from '@/types/activity'
import {
  getBannerField,
  getFirstBackgroundUrl,
  getTrimmedString,
  getTrimmedStringArray,
  resolveLocalizedTextValue,
} from '@/utils/activity-helpers'

const DYNAMIC_ACTIVITY_PATH = '/dynamic-activity'

const splitUrl = (url: string) => {
  const [urlWithoutHash, hash = ''] = url.split('#', 2)
  const [pathname, query = ''] = urlWithoutHash.split('?', 2)

  return { pathname, query, hash }
}

const isExternalUrl = (url: string) =>
  url.startsWith('http://') || url.startsWith('https://')

const isDynamicActivityUrl = (url?: string | null) => {
  const normalizedUrl = getTrimmedString(url)

  if (!normalizedUrl) {
    return false
  }

  const { pathname } = splitUrl(normalizedUrl)

  return pathname.endsWith(DYNAMIC_ACTIVITY_PATH)
}

const getV2BannerWebJumpUrl = (activity: Activity) =>
  getTrimmedString(
    activity.options?.v2?.banner?.button?.jump_target?.web_jump_url
  )

const getLegacyDynamicActivityImageUrls = (activity: Activity) => {
  const firstItem = activity.info?.background_url_list?.[0]
  const urls = getTrimmedStringArray(firstItem?.urls)

  if (urls.length > 0) {
    return urls
  }

  const singleUrl = getTrimmedString(firstItem?.url)

  return singleUrl ? [singleUrl] : []
}

const getV2LandingComponents = (activity: Activity) =>
  activity.options?.v2?.landing?.components ??
  activity.options?.v2?.landing?.blocks ??
  []

const extractImageUrlsFromLandingComponent = (
  component: ActivityV2LandingComponent
) => {
  const imageUrls = [
    getTrimmedString(component.image_url),
    getTrimmedString(component.imageUrl),
    getTrimmedString(component.background_url),
    getTrimmedString(component.backgroundUrl),
    getTrimmedString(component.url),
    ...getTrimmedStringArray(component.urls),
    ...getTrimmedStringArray(component.images),
  ].filter((item): item is string => Boolean(item))

  return Array.from(new Set(imageUrls))
}

const getV2LandingImageUrls = (activity: Activity) => {
  const components = getV2LandingComponents(activity)

  if (!Array.isArray(components)) {
    return []
  }

  return Array.from(
    new Set(
      components.flatMap((component) => {
        if (!component || typeof component !== 'object') {
          return []
        }

        return extractImageUrlsFromLandingComponent(component)
      })
    )
  )
}

const getV2HeroImageUrl = (banner: ActivityV2BannerConfig | undefined) =>
  getBannerField(banner, [
    'background_image',
    'hero_image_url',
    'heroImage',
    'background_url',
    'backgroundUrl',
  ]) || getFirstBackgroundUrl(banner?.background_url_list)

const parseTimestampNumber = (value: string | number | null | undefined) => {
  const normalizedValue =
    typeof value === 'number'
      ? String(value)
      : typeof value === 'string'
        ? value.trim()
        : ''

  if (!normalizedValue) {
    return undefined
  }

  const parsedValue = Number(normalizedValue)

  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return undefined
  }

  return parsedValue > 1_000_000_000_000 ? parsedValue : parsedValue * 1000
}

const LEGACY_ACTIVITY_TIME_PATTERN =
  /^\d{4}-\d{2}-\d{2}(?:[ T])\d{2}:\d{2}(?::\d{2})?$/

const parseLegacyActivityTimestamp = (value: string) => {
  if (!LEGACY_ACTIVITY_TIME_PATTERN.test(value)) {
    return undefined
  }

  const parsedValue = new Date(`${value.replace(' ', 'T')}+08:00`).getTime()

  return Number.isFinite(parsedValue) ? parsedValue : undefined
}

export const parseActivityTimestamp = (
  value: string | number | null | undefined
) => {
  const timestampValue = parseTimestampNumber(value)

  if (timestampValue) {
    return timestampValue
  }

  const normalizedValue = getTrimmedString(value)

  if (!normalizedValue) {
    return undefined
  }

  const legacyTimestamp = parseLegacyActivityTimestamp(normalizedValue)

  if (legacyTimestamp) {
    return legacyTimestamp
  }

  const parsedValue = new Date(normalizedValue).getTime()

  return Number.isFinite(parsedValue) ? parsedValue : undefined
}

export const parsePreferredActivityTimestamp = (
  timestampValue: string | number | null | undefined,
  legacyValue?: string | number | null
) => {
  return (
    parseActivityTimestamp(timestampValue) ||
    parseActivityTimestamp(legacyValue)
  )
}

const getBannerTimestamp = (
  banner: ActivityV2BannerConfig | undefined,
  fieldNames: string[]
) => {
  if (!banner) {
    return undefined
  }

  for (const fieldName of fieldNames) {
    const parsedValue = parseTimestampNumber(
      banner[fieldName] as string | number | null | undefined
    )

    if (parsedValue) {
      return parsedValue
    }
  }

  return undefined
}

const hasButtonVisualStyle = (style?: ActivityButtonVisualStyle) =>
  Boolean(
    style &&
    (style.background_color ||
      style.background_image_url ||
      typeof style.background_opacity === 'number' ||
      typeof style.overlay_enabled === 'boolean' ||
      style.top_overlay_color ||
      typeof style.top_overlay_opacity === 'number' ||
      style.bottom_overlay_color ||
      typeof style.bottom_overlay_opacity === 'number' ||
      style.text_color ||
      typeof style.font_size === 'number' ||
      typeof style.font_weight === 'number')
  )

const buildButtonVisualStyle = (
  button?: ActivityV2ButtonConfig
): ActivityButtonVisualStyle | undefined => {
  if (!button) {
    return undefined
  }

  const visualStyle: ActivityButtonVisualStyle = {
    background_color: getTrimmedString(button.style?.background_color),
    background_image_url: getTrimmedString(button.style?.background_image),
    background_opacity:
      typeof button.style?.background_opacity === 'number'
        ? button.style.background_opacity
        : undefined,
    overlay_enabled:
      typeof button.style?.overlay_enabled === 'boolean'
        ? button.style.overlay_enabled
        : undefined,
    top_overlay_color: getTrimmedString(button.style?.top_overlay_color),
    top_overlay_opacity:
      typeof button.style?.top_overlay_opacity === 'number'
        ? button.style.top_overlay_opacity
        : undefined,
    bottom_overlay_color: getTrimmedString(button.style?.bottom_overlay_color),
    bottom_overlay_opacity:
      typeof button.style?.bottom_overlay_opacity === 'number'
        ? button.style.bottom_overlay_opacity
        : undefined,
    text_color: getTrimmedString(button.text_style?.text_color),
    font_size:
      typeof button.text_style?.font_size === 'number'
        ? button.text_style.font_size
        : undefined,
    font_weight:
      typeof button.text_style?.font_weight === 'number'
        ? button.text_style.font_weight
        : undefined,
  }

  return hasButtonVisualStyle(visualStyle) ? visualStyle : undefined
}

const appendQueryParam = (
  params: URLSearchParams,
  key: string,
  value: string | number | undefined
) => {
  if (value === undefined || value === null || value === '') {
    return
  }

  params.set(key, String(value))
}

export type ActivityPreviewVisibilityReason =
  | 'public'
  | 'login_required'
  | 'not_whitelisted'

export const resolveActivityPreviewVisibility = (
  activity: Pick<ActivityDetailPageRes, 'options'>,
  userId?: string | null
): ActivityPreviewVisibilityReason => {
  const preview = (activity.options?.v2?.preview ??
    activity.options?.preview) as ActivityV2PreviewConfig | undefined

  if (!preview) {
    return 'public'
  }

  const releaseMode =
    typeof preview.release_mode === 'string' ? preview.release_mode.trim() : ''

  if (!releaseMode || releaseMode === 'public') {
    return 'public'
  }

  if (releaseMode !== 'preview_whitelist') {
    return 'public'
  }

  const normalizedUserId = typeof userId === 'string' ? userId.trim() : ''

  if (!normalizedUserId) {
    return 'login_required'
  }

  const whitelistUIDs = Array.isArray(preview.whitelist_uids)
    ? preview.whitelist_uids
        .map((uid) => (typeof uid === 'string' ? uid.trim() : ''))
        .filter(Boolean)
    : []

  return whitelistUIDs.includes(normalizedUserId) ? 'public' : 'not_whitelisted'
}

export const isHomeActivityBannerVisible = (
  activity: Activity,
  userId?: string | null,
  now = Date.now()
) => {
  const v2Banner = activity.options?.v2?.banner
  const startTime =
    getBannerTimestamp(v2Banner, ['display_start_time']) ||
    parsePreferredActivityTimestamp(
      activity.banner?.start_time_ts,
      activity.banner?.start_time
    )
  const endTime =
    getBannerTimestamp(v2Banner, ['display_end_time']) ||
    parsePreferredActivityTimestamp(
      activity.banner?.end_time_ts,
      activity.banner?.end_time
    )

  if (startTime === undefined || endTime === undefined) {
    return false
  }

  if (now < startTime || now > endTime) {
    return false
  }

  return resolveActivityPreviewVisibility(activity, userId) === 'public'
}

type ActivityHomeBannerDisplayScope = 'home' | 'world'

const DEFAULT_ACTIVITY_HOME_BANNER_DISPLAY_SCOPES: ActivityHomeBannerDisplayScope[] =
  ['home', 'world']

export const getActivityHomeBannerDisplayScope = (
  fromScreen?: string
): ActivityHomeBannerDisplayScope => (fromScreen === 'World' ? 'world' : 'home')

const normalizeActivityHomeBannerDisplayScopes = (
  value: unknown
): ActivityHomeBannerDisplayScope[] => {
  if (!Array.isArray(value)) {
    return DEFAULT_ACTIVITY_HOME_BANNER_DISPLAY_SCOPES
  }

  const scopeSet = new Set<ActivityHomeBannerDisplayScope>()
  value.forEach((scope) => {
    if (scope === 'home' || scope === 'world') {
      scopeSet.add(scope)
    }
  })

  if (scopeSet.size === 0) {
    return DEFAULT_ACTIVITY_HOME_BANNER_DISPLAY_SCOPES
  }

  return DEFAULT_ACTIVITY_HOME_BANNER_DISPLAY_SCOPES.filter((scope) =>
    scopeSet.has(scope)
  )
}

export const isHomeActivityBannerVisibleForScope = (
  activity: Activity,
  userId: string | null | undefined,
  scope: ActivityHomeBannerDisplayScope,
  now = Date.now()
) =>
  isHomeActivityBannerVisible(activity, userId, now) &&
  normalizeActivityHomeBannerDisplayScopes(
    activity.options?.v2?.banner?.display_scopes ??
      activity.banner?.display_scopes
  ).includes(scope)

export const buildHomeActivityBannerCardViewModel = (
  activity: Activity,
  lang?: string
): ActivityHomeBannerCardViewModel => {
  const v2Banner = activity.options?.v2?.banner
  const bannerCtaLabel = resolveLocalizedTextValue(v2Banner?.button?.text, lang)
  const legacyCtaLabel = getTrimmedString(activity.float_button?.description)
  const ctaLabel = bannerCtaLabel || legacyCtaLabel
  // Homepage banner CTA should only consume banner-level button visuals.
  const ctaStyle = buildButtonVisualStyle(v2Banner?.button)
  const jumpUrl =
    resolveActivityWebJumpUrl(activity) ||
    getTrimmedString(activity.banner?.web_jump_url)

  return {
    background_image_url:
      getV2HeroImageUrl(v2Banner) ||
      getTrimmedString(activity.banner?.background_url) ||
      getFirstBackgroundUrl(activity.banner?.background_url_list),
    header_image_url: getTrimmedString(v2Banner?.header_image),
    title:
      resolveLocalizedTextValue(v2Banner?.primary_text, lang) ||
      getBannerField(v2Banner, ['title']) ||
      getTrimmedString(activity.banner?.description),
    subtitle: resolveLocalizedTextValue(v2Banner?.secondary_text, lang),
    event_start_time:
      getBannerTimestamp(v2Banner, ['event_start_time']) ||
      parsePreferredActivityTimestamp(
        activity.info?.start_time_ts,
        activity.info?.start_time
      ),
    event_end_time:
      getBannerTimestamp(v2Banner, ['event_end_time']) ||
      parsePreferredActivityTimestamp(
        activity.info?.end_time_ts,
        activity.info?.end_time
      ),
    cta_label: ctaLabel,
    cta_style: ctaStyle,
    jump_url: jumpUrl || undefined,
  }
}

export const hasHomeActivityV2Banner = (activity: Activity) => {
  const banner = activity.options?.v2?.banner
  const ignoredKeys = new Set(['badge_text', 'badgeText'])

  return Boolean(
    banner &&
    typeof banner === 'object' &&
    Object.keys(banner).some((key) => {
      if (ignoredKeys.has(key)) {
        return false
      }

      const value = banner[key]

      if (typeof value === 'string') {
        return Boolean(value.trim())
      }

      if (typeof value === 'number') {
        return Number.isFinite(value)
      }

      if (value && typeof value === 'object') {
        return Object.keys(value).length > 0
      }

      return false
    })
  )
}

export const resolveLegacyActivityWebJumpUrl = (activity: Activity) => {
  const jumpUrl = getTrimmedString(activity.banner?.web_jump_url)

  if (!jumpUrl) {
    return null
  }

  if (jumpUrl !== DYNAMIC_ACTIVITY_PATH) {
    return jumpUrl
  }

  const { pathname, query, hash } = splitUrl(jumpUrl)
  const params = new URLSearchParams(query)
  const firstItem = activity.info?.background_url_list?.[0]
  const floatButton = activity.float_button

  if (firstItem?.urls?.length) {
    params.set('urls', firstItem.urls.filter(Boolean).join(','))
  }

  const buttonBgUrl = getTrimmedString(
    floatButton?.background_url_list?.[0]?.url
  )
  if (buttonBgUrl) {
    params.set('buttonBg', buttonBgUrl)
  }

  const buttonLink = getTrimmedString(floatButton?.web_jump_url)
  if (buttonLink) {
    params.set('buttonLink', buttonLink)
  }

  const queryString = params.toString()
  const hashSuffix = hash ? `#${hash}` : ''

  return `${pathname}${queryString ? `?${queryString}` : ''}${hashSuffix}`
}

export const resolveV2ActivityWebJumpUrl = (activity: Activity) => {
  const jumpUrl =
    getV2BannerWebJumpUrl(activity) ||
    getTrimmedString(activity.banner?.web_jump_url)

  if (!jumpUrl) {
    return null
  }

  if (!isDynamicActivityUrl(jumpUrl)) {
    return jumpUrl
  }

  const { pathname, query, hash } = splitUrl(jumpUrl)
  const params = new URLSearchParams(query)
  const legacyImageUrls = getLegacyDynamicActivityImageUrls(activity)
  const v2LandingImageUrls = getV2LandingImageUrls(activity)
  const v2Banner = activity.options?.v2?.banner
  const heroImageUrl =
    getV2HeroImageUrl(v2Banner) ||
    (v2LandingImageUrls.length > 0 ? legacyImageUrls[0] : undefined)
  const landingImageUrls =
    v2LandingImageUrls.length > 0 ? v2LandingImageUrls : legacyImageUrls
  const title =
    getBannerField(v2Banner, ['title']) ||
    getTrimmedString(activity.banner?.description)
  const subtitle = undefined

  appendQueryParam(
    params,
    'schema_version',
    getTrimmedString(activity.options?.schema_version) ||
      activity.options?.schema_version
  )
  appendQueryParam(params, 'activityId', getTrimmedString(activity.activity_id))
  appendQueryParam(params, 'title', title)
  appendQueryParam(params, 'subtitle', subtitle)
  appendQueryParam(params, 'heroImage', heroImageUrl)

  if (landingImageUrls.length > 0) {
    params.set('urls', landingImageUrls.join(','))
  }

  const queryString = params.toString()
  const hashSuffix = hash ? `#${hash}` : ''

  if (isExternalUrl(jumpUrl)) {
    return `${pathname}${queryString ? `?${queryString}` : ''}${hashSuffix}`
  }

  return `${pathname}${queryString ? `?${queryString}` : ''}${hashSuffix}`
}

export const resolveActivityWebJumpUrl = (activity: Activity) => {
  if (hasHomeActivityV2Banner(activity)) {
    return resolveV2ActivityWebJumpUrl(activity)
  }

  return resolveLegacyActivityWebJumpUrl(activity)
}
